import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { canAccessPreceptor } from '../services/authService';
import { getSavedCount } from '../services/articleService';
import { getFlashcardSummary } from '../services/flashcardService';
import { useContentLang } from '../hooks/useContentLang';

interface SidebarProps {
  mobileOpen?: boolean;
  onNavigate?: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  home: <path d="M3 12l9-9 9 9M5 10v10h14V10" />,
  feed: <path d="M4 6h16M4 12h16M4 18h10" />,
  resumos: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>
  ),
  casos: (
    <>
      <path d="M12 3l8 4v5c0 4.5-3.4 7.6-8 9-4.6-1.4-8-4.5-8-9V7z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),
  videos: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9l5 3-5 3z" />
    </>
  ),
  temas: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
  flashcards: (
    <>
      <rect x="3" y="6" width="14" height="12" rx="2" />
      <path d="M7 4h14v12" />
    </>
  ),
  preceptor: (
    <>
      <path d="M12 3a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V7a4 4 0 0 1 4-4z" />
      <path d="M5 21a7 7 0 0 1 14 0" />
      <path d="M19 8l2 1-2 1M5 8l-2 1 2 1" />
    </>
  ),
  biblioteca: (
    <>
      <path d="M6 4h9l3 3v13H6z" />
      <path d="M9 12h6M9 16h4" />
    </>
  ),
  conteudo: (
    <>
      <path d="M4 20h4l10-10-4-4L4 16z" />
      <path d="M13 7l4 4" />
    </>
  ),
  usuarios: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 11a3 3 0 1 0 0-6M18 20a5 5 0 0 0-3-4.6" />
    </>
  ),
  termos: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M8.5 15.5l2 2 4.5-4.5" />
    </>
  ),
  config: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </>
  ),
};

function initials(name: string | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onNavigate }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const lang = useContentLang();
  const location = useLocation();
  const tipo = new URLSearchParams(location.search).get('tipo');

  const { data: savedCount } = useQuery({
    queryKey: ['savedCount'],
    queryFn: getSavedCount,
    enabled: Boolean(user),
  });

  // Mesma query da tela de Flashcards: marcar artigo como lido e sessões
  // invalidam ['flashcardSummary'] e o contador do menu acompanha.
  const { data: flashcardSummary } = useQuery({
    queryKey: ['flashcardSummary'],
    queryFn: getFlashcardSummary,
    enabled: Boolean(user),
  });

  // Feed e seus atalhos compartilham /feed; o ativo depende de ?tipo=.
  const feedActive = (target: string | null) =>
    location.pathname === '/feed' && (tipo ?? null) === target;

  const item = (
    key: string,
    to: string,
    label: string,
    opts: { active?: boolean; end?: boolean; pill?: React.ReactNode; testId?: string } = {},
  ) => (
    <NavLink
      key={key}
      to={to}
      end={opts.end}
      onClick={onNavigate}
      data-testid={opts.testId}
      className={({ isActive }) =>
        `nav-item ${(opts.active ?? isActive) ? 'active' : ''}`
      }
    >
      <svg viewBox="0 0 24 24">{ICONS[key]}</svg>
      <span>{label}</span>
      {opts.pill}
    </NavLink>
  );

  return (
    <>
      {mobileOpen && <div className="nn-sidebar-backdrop" onClick={onNavigate} />}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`} aria-label={t('news.nav.aria')}>
        <NavLink to="/" className="brand" onClick={onNavigate}>
          <img
            className="logo"
            src={lang === 'es' ? '/assets/logos/neuronews-es.png' : '/assets/logos/neuronews-pt.png'}
            alt={lang === 'es' ? 'NeuroNews Latam' : 'NeuroNews'}
          />
        </NavLink>

        <nav className="nn-nav">
          {item('home', '/', t('news.nav.home'), { end: true })}
          {item('feed', '/feed', t('news.nav.feed'), { active: feedActive(null) })}
          {item('resumos', '/feed?tipo=artigo', t('news.nav.resumos'), { active: feedActive('artigo') })}
          {item('casos', '/feed?tipo=caso', t('news.nav.casos'), { active: feedActive('caso') })}
          {lang === 'pt' &&
            item('videos', '/feed?tipo=video', t('news.nav.videos'), { active: feedActive('video') })}
          {item('temas', '/temas', t('news.nav.temas'))}

          <div className="nav-sec">{t('news.nav.study')}</div>
          {item('flashcards', '/flashcards', t('sidebar.flashcards'), {
            pill: flashcardSummary?.total ? (
              <span className="pill" data-testid="flashcards-count">{flashcardSummary.total}</span>
            ) : undefined,
          })}
          {canAccessPreceptor(user) &&
            item('preceptor', '/preceptor', t('sidebar.preceptorAI'), {
              pill: <span className="pill ai">IA</span>,
            })}
          {item('biblioteca', '/biblioteca', t('news.nav.library'), {
            pill: savedCount ? <span className="pill">{savedCount}</span> : undefined,
          })}

          {user?.role === 'admin' && (
            <>
              <div className="nav-sec">{t('news.nav.admin')}</div>
              {item('conteudo', '/admin/conteudos', t('news.nav.adminContent'))}
              {item('usuarios', '/admin/users', t('sidebar.adminUsers'))}
            {item('termos', '/admin/termos', t('sidebar.adminLegal'))}
            </>
          )}
        </nav>

        <div className="nav-spacer" />

        {item('config', '/settings', t('sidebar.settings'), { testId: 'settings-button' })}

        {user && (
          <div className="nav-user">
            <div className="av">{initials(user.name)}</div>
            <div>
              <div className="nm">{user.name}</div>
              <div className="rl">{user.email}</div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
